/**
 * Sidebar Navigation for the Farm Dashboard Layout.
 * Dark teal rounded container with white text, pill-shaped menu items.
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Dashboard,
  WaterDrop,
  Sensors,
  Cloud,
  Notifications,
  CalendarToday,
  Map,
  ContentPaste,
  EventNote,
  ListAlt,
  Home,
  Close,
  Settings,
  Router as RouterIcon,
  Forest,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens } from '@/theme/tokens';
import SaqiaLogo from './SaqiaLogo';
import { useBackendVersion } from '@/hooks/useBackendVersion';
import { useViewerLink } from '@/hooks/useViewerLink';
import { APP_BUILD } from '@/services/apiClient';

interface SidebarNavProps {
  onClose?: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ onClose }) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const navigate = useNavigate();
  const location = useLocation();
  const { farmId } = useParams<{ farmId: string }>();
  const backend = useBackendVersion();
  const viewerLink = useViewerLink(farmId);

  const consultaItems = [
    { text: t('navigation.dashboard'), icon: <Dashboard />, path: `/farms/${farmId}/dashboard` },
    { text: t('navigation.waterBalance'), icon: <WaterDrop />, path: `/farms/${farmId}/water-balance` },
    { text: t('navigation.devices'), icon: <Sensors />, path: `/farms/${farmId}/sensors` },
    { text: t('navigation.controllers', 'Controladores'), icon: <RouterIcon />, path: `/farms/${farmId}/controllers` },
    { text: t('navigation.map'), icon: <Map />, path: `/farms/${farmId}/map` },
    { text: t('navigation.weather'), icon: <Cloud />, path: `/farms/${farmId}/weather` },
    { text: t('navigation.alerts'), icon: <Notifications />, path: `/farms/${farmId}/alerts` },
    { text: t('navigation.programs', 'Programas'), icon: <EventNote />, path: `/farms/${farmId}/programs` },
    { text: t('navigation.irrigationLog', 'Registro'), icon: <ListAlt />, path: `/farms/${farmId}/irrigation-log` },
  ];

  const setupItems = [
    { text: t('navigation.irrigationSeasons'), icon: <CalendarToday />, path: `/farms/${farmId}/seasons` },
    { text: t('navigation.fields'), icon: <Map />, path: `/farms/${farmId}/layout` },
    { text: t('navigation.schedules'), icon: <ContentPaste />, path: `/farms/${farmId}/schedules/constraints` },
    { text: t('navigation.settings', 'Settings'), icon: <Settings />, path: `/farms/${farmId}/notifications` },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNav = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const navItemSx = (path: string) => ({
    borderRadius: '24px',
    mx: 1,
    mb: 0.5,
    color: isActive(path) ? tokens.colors.sidebar.textActive : tokens.colors.sidebar.text,
    '&:hover': {
      bgcolor: tokens.colors.sidebar.bgHover,
    },
    ...(isActive(path) && {
      bgcolor: tokens.colors.sidebar.bgActive,
      '&:hover': {
        bgcolor: tokens.colors.sidebar.bgActive,
      },
    }),
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: tokens.colors.sidebar.bg,
        color: tokens.colors.sidebar.text,
        borderRadius: '24px',
        // Scroll rather than clip: on a phone the menu is taller than the
        // screen, and the build line at the bottom — the one thing support
        // asks an operator to read — was cut off with no way to reach it.
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      {/* Close button (mobile) + Logo */}
      <Box sx={{ px: 2.5, pt: onClose ? 1.5 : 3, pb: onClose ? 1 : 3, display: 'flex', flexDirection: 'column' }}>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{ color: tokens.colors.sidebar.text, alignSelf: 'flex-start', mb: 1 }}
          >
            <Close />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleNav('/')}>
          <SaqiaLogo />
        </Box>
      </Box>

      {/* CONSULTA section */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{ px: 2.5, mb: 1, display: 'block', color: tokens.colors.sidebar.sectionLabel, fontWeight: 600, letterSpacing: '0.08em' }}
        >
          {t('sidebar.consulta')}
        </Typography>
        <List disablePadding>
          {consultaItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                sx={navItemSx(item.path)}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive(item.path) ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {viewerLink && (
            <ListItem disablePadding>
              <ListItemButton
                component="a"
                href={viewerLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onClose?.()}
                sx={navItemSx(viewerLink)}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><Forest /></ListItemIcon>
                <ListItemText
                  primary={t('sidebar.lidarViewer')}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
                <OpenInNew sx={{ fontSize: '0.9rem', opacity: 0.7 }} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>

      {/* SET UP section */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{ px: 2.5, mb: 1, display: 'block', color: tokens.colors.sidebar.sectionLabel, fontWeight: 600, letterSpacing: '0.08em' }}
        >
          {t('sidebar.setup')}
        </Typography>
        <List disablePadding>
          {setupItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                sx={navItemSx(item.path)}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive(item.path) ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Bottom section */}
      <List disablePadding>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNav('/farms')}
            sx={{
              mx: 1, mb: 0.5, borderRadius: '24px',
              color: tokens.colors.sidebar.textMuted,
              '&:hover': { bgcolor: tokens.colors.sidebar.bgHover, color: '#fff' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><Home /></ListItemIcon>
            <ListItemText
              primary={t('sidebar.backHome')}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Typography
          variant="caption"
          sx={{ color: tokens.colors.sidebar.textMuted, display: 'block' }}
          title={`commit ${__APP_COMMIT__} · built ${__BUILD_TIMESTAMP__}`}
        >
          {/* The commit, visibly: every build is v1.0.0, and a tooltip
              cannot be read on a phone. Same string the app sends as
              X-App-Version and the command history shows. */}
          UI v{APP_BUILD}{backend.version ? ` · API v${backend.version}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.colors.sidebar.textMuted, fontSize: '0.65rem', opacity: 0.7 }}>
          {(() => {
            try {
              const d = new Date(__BUILD_TIMESTAMP__);
              if (isNaN(d.getTime())) return '';
              return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
            } catch { return ''; }
          })()}
        </Typography>
      </Box>
    </Box>
  );
};

export default SidebarNav;
